import { useCallback, useEffect, useState } from 'react';

/**
 * Sidebar collapsed-or-not, persisted to localStorage. Mirrors `useTheme`'s
 * shape — value loads synchronously from localStorage on first render so
 * the layout doesn't briefly flash at the wrong width before hydrating.
 */
const STORAGE_KEY = 'orp-sidebar-collapsed';

function readStored(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function useSidebar() {
  const [collapsed, setCollapsedState] = useState<boolean>(() => readStored());

  // Keep multiple Shell mounts (unlikely, but cheap to support) in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setCollapsedState(e.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setCollapsed = useCallback((next: boolean) => {
    try {
      if (next) localStorage.setItem(STORAGE_KEY, '1');
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    setCollapsedState(next);
  }, []);

  const toggle = useCallback(() => setCollapsed(!collapsed), [collapsed, setCollapsed]);

  return { collapsed, setCollapsed, toggle };
}
